import path from "node:path";
import { pathToFileURL } from "node:url";
import { createJiti } from "jiti";
import { z } from "zod";
import type { PromptContent, PromptOverride, ReviewPromptConfig } from "./agent.js";
import { defaultDiffLimits } from "./diff.js";

const commandSchema = z.union([z.string().min(1), z.array(z.string().min(1))]);
const promptContentSchema = z.union([
  z.string().min(1),
  z.custom<Exclude<PromptContent, string>>((value) => typeof value === "function", "Expected a prompt callback"),
]);
const promptOverrideSchema: z.ZodType<PromptOverride> = z.object({
  mode: z.enum(["append", "replace"]).optional(),
  content: promptContentSchema,
});
const reviewPromptConfigSchema: z.ZodType<ReviewPromptConfig> = z.object({
  root: z.union([promptContentSchema, promptOverrideSchema]).optional(),
  review: z.union([promptContentSchema, promptOverrideSchema]).optional(),
  note: promptContentSchema.optional(),
  notes: z.array(promptContentSchema).optional(),
});
export const claudeCodeModelSchema = z.enum(["fable", "sonnet", "opus", "haiku"]);
const claudeCodeAdapterSchema = z.object({
  adapter: z.literal("claude-code"),
  model: claudeCodeModelSchema.optional(),
  executable: z.string().min(1).optional(),
});
const reviewerAgentSchema = claudeCodeAdapterSchema.extend({
  prompt: reviewPromptConfigSchema.optional(),
});
const plannerAgentSchema = claudeCodeAdapterSchema.extend({
  prompt: z.union([promptContentSchema, promptOverrideSchema]).optional(),
  required: z.boolean().optional().default(true),
});
const agentsSchema = z.object({
  reviewer: reviewerAgentSchema.default({ adapter: "claude-code" }),
  planner: plannerAgentSchema.optional(),
});
const defaultDiffConfig = { exclude: [], ...defaultDiffLimits };
const diffConfigSchema = z.object({
  /** Glob patterns excluded from the diff, on top of the built-in defaults (lockfiles, minified output, source maps). */
  exclude: z.array(z.string().min(1)).default([]),
  maxFileChangedLines: z.number().int().positive().default(defaultDiffLimits.maxFileChangedLines),
  maxTotalBytes: z.number().int().positive().default(defaultDiffLimits.maxTotalBytes),
});

export const uiReviewConfigSchema = z.object({
  install: commandSchema.optional(),
  setup: commandSchema.optional(),
  start: z.string().min(1),
  ready: z.string().url(),
  stop: commandSchema.optional(),
  timeoutMs: z.number().int().positive().default(120_000),
  diff: diffConfigSchema.default(defaultDiffConfig),
  agents: agentsSchema.default({ reviewer: { adapter: "claude-code" } }),
});

export type ClaudeCodeModel = z.infer<typeof claudeCodeModelSchema>;

export interface ClaudeCodeAdapterConfig {
  adapter: "claude-code";
  model?: ClaudeCodeModel;
  executable?: string;
}

export interface ReviewerAgentConfig extends ClaudeCodeAdapterConfig {
  prompt?: ReviewPromptConfig;
}

export interface PlannerAgentConfig extends ClaudeCodeAdapterConfig {
  prompt?: PromptContent | PromptOverride;
  required?: boolean;
}

export interface AgentsConfigInput {
  reviewer?: ReviewerAgentConfig;
  planner?: PlannerAgentConfig;
}

export interface AgentsConfig {
  reviewer: ReviewerAgentConfig;
  planner?: PlannerAgentConfig & { required: boolean };
}

export interface DiffConfigInput {
  /** Glob patterns excluded from the diff, on top of the built-in defaults (lockfiles, minified output, source maps). */
  exclude?: string[];
  maxFileChangedLines?: number;
  maxTotalBytes?: number;
}

export interface DiffConfig {
  exclude: string[];
  maxFileChangedLines: number;
  maxTotalBytes: number;
}

export interface UiReviewConfigInput {
  install?: string | string[];
  setup?: string | string[];
  start: string;
  ready: string;
  stop?: string | string[];
  timeoutMs?: number;
  diff?: DiffConfigInput;
  agents?: AgentsConfigInput;
}

export interface UiReviewConfig extends Omit<UiReviewConfigInput, "timeoutMs" | "diff" | "agents"> {
  timeoutMs: number;
  diff: DiffConfig;
  agents: AgentsConfig;
}

export function defineConfig(config: UiReviewConfigInput): UiReviewConfigInput {
  return config;
}

export async function loadConfig(cwd: string, configPath?: string): Promise<UiReviewConfig> {
  const absolutePath = path.resolve(cwd, configPath ?? "revuiw.config.ts");
  const jiti = createJiti(pathToFileURL(import.meta.url).href);
  const loaded = await jiti.import(absolutePath, { default: true });
  return uiReviewConfigSchema.parse(loaded) as UiReviewConfig;
}
